import type { RootState } from '@/app/store'
import Button from '@/components/Button'
import DialogPopup from '@/components/Dialog/DialogPopup'
import Input from '@/components/Form/Input'
import LanguageSelect from '@/components/Form/LanguageSelect'
import Password from '@/components/Form/Password'
import Layout from '@/components/Layout'
import Loading from '@/components/Loading'
import { availableState } from '@/features/authSlice'
import { dialogRegisterQuestionnaireState } from '@/features/dialogSlice'
import { urlcampaign, urllogin } from '@/lib/custom'
import { apiGetNoneAuth } from '@/lib/originalFetchApi'
import type { NextPage } from 'next'
import Head from 'next/head'
import Link from 'next/link'
import { useRouter } from 'next/router'
import React, { useState, useEffect } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import type { SubmitHandler } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { PaakConversionPageLoader } from 'sdk-paak-conversion-page'

type Inputs = {
  username: string
  postalcode: string
  address: string
  password: string
  account: string
}

const Page: NextPage = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const router = useRouter()
  const [signinState, setSigninState] = useState<string | undefined>('')
  const [campaign, setCampaign] = useState<Api.CampaignResBody>()

  const availableStateSelector = useSelector((state: RootState) => state.auth.available)

  const loginTypeStateSelector = useSelector((state: RootState) => state.auth.loginType)

  useEffect(() => {
    if (localStorage.getItem(`${process.env.NEXT_PUBLIC_SERVICE_NAME}-jwt`)) {
      if (loginTypeStateSelector !== 9) {
        router.push({
          pathname: '/',
        })
      }
    }
    return
  }, [loginTypeStateSelector])

  const useFormMethods = useForm<Inputs>()

  const {
    register,
    handleSubmit,
    watch,
    getValues,
    formState: { errors },
  } = useFormMethods

  useEffect(() => {
    async function campaignFetch() {
      const hash = new Date().getTime()

      const res = await apiGetNoneAuth(`${urlcampaign}?${hash}&page=${window.location.pathname}`)
      if (res) {
        setCampaign(res.campaign)
        if (res.campaign.ad) {
          // 広告効果計測
          new PaakConversionPageLoader({
            backendEndpoint: process.env.NEXT_PUBLIC_AD_BACKEND_API_ENDPOINT,
            backendApiKey: res.campaign.adKey,
            isSetScript: false,
          })
            .load()
            .onConversion({
              userId: null,
            })
            .catch((v) => console.log(v))
        }
      }
    }

    campaignFetch()
  }, [])

  useEffect(() => {
    if (campaign && !campaign.isLocalize) {
      localStorage.setItem('i18nextLng', 'ja')
    }
  }, [campaign])

  const onSubmitDialog: SubmitHandler<Inputs> = async (data) => {
    dispatch(availableState(true))

    try {
      const authAccountFetch = await fetch(`${urllogin}`, {
        method: 'POST',
        headers: {
          'X-Raund-Token': `${process.env.NEXT_PUBLIC_RAUND_TOKEN}`,
          'X-Local': localStorage.getItem('i18nextLng'),
        },
        body: JSON.stringify({
          username: data.account,
          password: data.password,
        }),
      })

      const res = await authAccountFetch.json()
      const result = res.result
      console.log(JSON.stringify(result))

      if (result.status === 200) {
        localStorage.setItem(`${process.env.NEXT_PUBLIC_SERVICE_NAME}-jwt`, `${result.id_token}`)

        localStorage.setItem(`${process.env.NEXT_PUBLIC_SERVICE_NAME}-refresh`, `${result.refresh_token}`)

        localStorage.setItem(`${process.env.NEXT_PUBLIC_SERVICE_NAME}-username`, `${result.username}`)

        setSigninState('')
        dispatch(availableState(true))
        dispatch(dialogRegisterQuestionnaireState(true))

        router.push(`${process.env.NEXT_PUBLIC_SITEURL}/${process.env.NEXT_PUBLIC_SERVICE_NAME}/`)
        return
      }

      setSigninState(result.message)
      dispatch(availableState(false))
      return
    } catch (error) {
      setSigninState(t('login.not_registered', 'アカウント名とパスワードが登録されていません。'))
      dispatch(availableState(false))
    }
  }

  return (
    <Layout title="">
      <Head>
        <meta
          name="viewport"
          content="width=device-width,initial-scale=1.0,minimum-scale=1.0,maximum-scale=1.0,user-scalable=no"
        />
      </Head>

      <section className="w-full">
        <img src={campaign?.image} className="w-full" alt="" />
        <div className="mx-2 mt-10">
          <div className="mx-auto mb-[20px] flex w-11/12 items-center justify-center">
            <div className="h-[0.5px] w-[80%] bg-[#666]" />
            <span className="mx-[6px] whitespace-nowrap text-center text-[10px] font-light text-[#666]">
              {campaign?.isLineLogin ? t('login.register_with_line') : t('login.register')}
            </span>
            <div className="h-[0.5px] w-[80%] bg-[#666]" />
          </div>
          {campaign?.isLineLogin && (
            <div className="text-center">
              <a
                href={`${process.env.NEXT_PUBLIC_COGNITO_ENDPOINT}/oauth2/authorize?identity_provider=${campaign.cognitoIdentityProvider}&redirect_uri=${process.env.NEXT_PUBLIC_SITEURL}/${process.env.NEXT_PUBLIC_SERVICE_NAME}/auth/check/&response_type=CODE&client_id=${campaign.cognitoClientId}&scope=email openid profile`}
                className="mx-auto mb-[16px] block w-[71.7%]"
              >
                <img
                  src={`${process.env.NEXT_PUBLIC_SITEURL}/${process.env.NEXT_PUBLIC_SERVICE_NAME}/images/btn_line.png`}
                  alt=""
                />
              </a>
            </div>
          )}

          {/* 4はLINEのみ */}
          {loginTypeStateSelector !== 4 && (
            <>
              <div className="mx-auto w-11/12 px-6">
                {loginTypeStateSelector === 9 ? (
                  <Button
                    text={t('login.for_new_user')}
                    borderColor="border-main"
                    bgColor="bg-[#fff]"
                    onClick={() => router.reload()}
                  />
                ) : (
                  <Button
                    text={t('login.for_new_user')}
                    borderColor="border-main"
                    bgColor="bg-[#fff]"
                    href={`${process.env.NEXT_PUBLIC_SITEURL}/${process.env.NEXT_PUBLIC_SERVICE_NAME}/auth/register/`}
                  />
                )}
              </div>

              {loginTypeStateSelector !== 9 && (
                <div className="mx-auto mt-[24px] flex w-11/12 items-center justify-center">
                  <div className="h-[0.5px] w-[80%] bg-[#666]" />
                  <span className="mx-[6px] whitespace-nowrap text-center text-[10px] font-light text-[#666]">
                    {loginTypeStateSelector === 1
                      ? t('login.or_already_with_account_name', 'またはアカウント名でログイン')
                      : t('login.or_already_with_email', 'またはメールアドレスでログイン')}
                  </span>
                  <div className="h-[0.5px] w-[80%] bg-[#666]" />
                </div>
              )}
            </>
          )}

          <div
            className={`mx-auto mt-5 w-11/12 rounded-lg to-white px-6 py-7 ${
              loginTypeStateSelector === 4 ? '' : 'shadow-primary'
            }`}
          >
            {loginTypeStateSelector !== 4 && loginTypeStateSelector !== 9 && (
              <>
                {signinState !== '' ? <div className="questerror text-sm">{`${signinState}`}</div> : <></>}
                <FormProvider {...useFormMethods}>
                  <form className="mb-6" method="" onSubmit={handleSubmit(onSubmitDialog)}>
                    <fieldset>
                      {loginTypeStateSelector === 1 ? (
                        <div className="relative">
                          <Input
                            labelName={t('login.account_name')}
                            htmlfor="account"
                            type="text"
                            placeholder=""
                            register={register}
                            required
                            require={true}
                            ariaLabel="アカウント名"
                          />
                          <p className="text-xs text-color01">{t('login.account_name_notification')}</p>
                          {errors.account?.type === 'required' && (
                            <p className="error text-xss text-color01">
                              {t('login.account_name_error_message_input_required', '※入力してください')}
                            </p>
                          )}
                        </div>
                      ) : (
                        ''
                      )}

                      {loginTypeStateSelector === 2 ? (
                        <div className="relative">
                          <Input
                            labelName={t('login.email')}
                            htmlfor="account"
                            type="text"
                            placeholder=""
                            register={register}
                            validate={{
                              m: (v) =>
                                (v &&
                                  /^[A-Za-z0-9]{1}[A-Za-z0-9_.-]*@{1}[A-Za-z0-9_.-]{1,}\.[A-Za-z0-9]{1,}$/.test(v)) ||
                                t('login.email_error_message_format_error'),
                            }}
                            required
                            require={true}
                            ariaLabel="メールアドレス"
                          />
                          {errors.account?.type === 'm' && (
                            <p className="error text-xs text-color01">{errors.account?.message}</p>
                          )}
                          {errors.account?.type === 'required' && (
                            <p className="error text-xss text-color01">
                              {t('login.email_name_error_message_input_required')}
                            </p>
                          )}
                        </div>
                      ) : (
                        ''
                      )}

                      <div className="mt-6">
                        <Password
                          minLength={8}
                          validate={{
                            p: (v) => (v && /[0-9]/.test(v)) || t('login.password_error_message_number_required'),
                            e: (v) => (v && /[a-z]/.test(v)) || t('login.password_error_message_alphabet_required'),
                          }}
                        />
                      </div>
                    </fieldset>

                    <Button text={t('login.login')} type="submit" classes="mt-10" />
                  </form>
                </FormProvider>
              </>
            )}

            <div className="text-center text-sm sm:mb-4 sm:flex sm:flex-wrap">
              <Link href="https://raund.net/terms/?no_layout=true" passHref={true}>
                <a className="flex-2" target="_new" rel="noopener">
                  {t('login.terms')}
                </a>
              </Link>
            </div>
          </div>
          {campaign?.isLocalize ? (
            <div className="mx-auto mt-[24px] w-11/12">
              <LanguageSelect />
            </div>
          ) : null}
        </div>
      </section>

      {availableStateSelector ? <Loading /> : ''}

      <DialogPopup hookpoint="LOGIN" />
    </Layout>
  )
}

export default Page
